import React, { useMemo } from 'react';
import { DropMenu, type DropMenuItem } from 'venn-ui-kit';
import { cn } from '@/lib/utils';
import { getExtrapolateFromValue, type ProxyTypeValue } from 'venn-utils';

export type ProxyTypeDropdownItem<T extends ProxyTypeValue = ProxyTypeValue> = DropMenuItem<T>;

export interface ProxyTypeDropdownProps<T extends ProxyTypeValue = ProxyTypeValue> {
  value?: T;
  onSelect: (value: T) => void;
  items: ProxyTypeDropdownItem<T>[];
  placeholder?: string;
  label?: React.ReactNode;
  width?: number;
  testId?: string;
  className?: string;
}

export const ProxyTypeDropdown = <T extends ProxyTypeValue = ProxyTypeValue>({
  value,
  onSelect,
  items,
  placeholder = 'Proxy type',
  label,
  width = 350,
  testId = 'qa-proxy-type-dropdown',
  className,
}: ProxyTypeDropdownProps<T>) => {
  const selectedItem = useMemo(
    () =>
      value
        ? items.find(
            ({ value: itemValue }) =>
              itemValue.proxyType === value.proxyType &&
              getExtrapolateFromValue(itemValue) === getExtrapolateFromValue(value),
          )?.value
        : undefined,
    [items, value],
  );

  return (
    <div
      className={cn(
        'text-[14px]',
        '[&_.simple-menu-item]:text-[16px]',
        '[&_.simple-menu-item-description]:text-[12px]',
        '[&_.simple-menu-item-description]:leading-[16px]',
      )}
      data-testid={testId}
    >
      {label && (
        <div className={cn('font-bold leading-[24px]')} data-testid="dropdown-label">
          {label}
        </div>
      )}

      <DropMenu<T>
        width={width}
        height={400}
        items={items}
        selected={selectedItem}
        onChange={(item) => onSelect(item.value)}
        minimumItemsToTrigger={1}
        placeholder={placeholder}
        newStyle
        className={className}
      />
    </div>
  );
};
